import { config } from "config";
import { createSelector } from "reselect";

import { DetailedSource } from "interfaces/entities/ticket";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { IStoreState } from "interfaces/storeState";
import { createRequestFetchingSelector } from "store/requests/selectors";

import { selectIsAdmin, selectIsAgent } from "../agents";
import { IWithBillingEntitiesState } from "../billing";
import { IWithIntegrationsState, selectHasLiveChatIntegration, selectShopifyIntegration } from "../integrations";
import { IMailboxReplyAddress } from "../mailboxes/interfaces";

import {
  IGoogleForwardingConfig,
  ILicense,
  ILicenseSettings,
  ILicenseSettingsCustomFields,
  ILicenseSourceParams,
} from "./interfaces";

export interface IWithLicensesState {
  entities: {
    license: ILicense;
  };
}

export enum DataStatus {
  Loading = "loading",
  NotNeeded = "not-needed",
  Error = "error",
  Ready = "success",
}

export type IWithLicensesAndBillingState = IWithLicensesState & IWithBillingEntitiesState;

export function selectLicense(state: IWithLicensesState): ILicense {
  return state.entities.license;
}

export function selectLicenseId(state: IWithLicensesState): number {
  return selectLicense(state).id;
}

export function selectLicenseSettings(state: IWithLicensesState): ILicenseSettings {
  return selectLicense(state).settings;
}

export function selectDefaultTeamId(state: IWithLicensesState): string {
  return selectLicense(state).defaultTeamId;
}

export function selectDefaultTemplateId(state: IWithLicensesState): string {
  return selectLicense(state).defaultTemplateId;
}

export function selectIsKbWidgetPreinstalled(state: IWithLicensesState): boolean {
  const license = selectLicense(state);
  return license ? Boolean(license.flags.knowledgeBaseIntegrationPreinstalled) : false;
}
export function selectDefaultMailboxUsed(state: IWithLicensesState): boolean {
  const license = selectLicense(state);
  return license ? license.flags.defaultInboxUsed : false;
}
export function selectCustomDomainAdded(state: IWithLicensesState): boolean {
  const license = selectLicense(state);
  return license ? license.flags.customDomainAdded : false;
}
export function selectFirstAgentAdded(state: IWithLicensesState): boolean {
  const license = selectLicense(state);
  return license ? license.flags.firstAgentAdded : false;
}
export function selectFirstTeamAdded(state: IWithLicensesState): boolean {
  const license = selectLicense(state);
  return license ? license.flags.firstTeamAdded : false;
}
export function selectFirstTicketResponded(state: IWithLicensesState): boolean {
  const license = selectLicense(state);
  return license ? license.flags.firstTicketResponded : false;
}

export function selectIsLicenseVerified(state: IStoreState): boolean {
  const shopifyIntegration = selectShopifyIntegration(state);

  const { verifiedAt, verifiedBy } = selectLicense(state);

  return !!shopifyIntegration || (verifiedAt !== null && verifiedBy !== null);
}

export function selectIsPastDue(state: IWithLicensesState): boolean {
  return selectLicense(state).subscription.pastDue;
}

export function selectManagedByPartner(state: IWithLicensesState): boolean {
  return selectLicense(state).settings.managedByPartner;
}

export function selectManagedByPartnerEmail(state: IWithLicensesState): string {
  return selectLicense(state).settings.partnersContactEmail;
}

export function selectDefaultReplyAddress(state: IWithLicensesState): IMailboxReplyAddress | null {
  return selectLicense(state).settings.replyAddress;
}

export function selectDefaultMailboxAddress(state: IWithLicensesState): string {
  return `${state.entities.license.id}@${config.defaultMailboxSuffix}`;
}

export function selectCompanyName(state: IWithLicensesState): string {
  return selectLicense(state).settings.companyName;
}

export function selectIsCompanyNameSet(state: IWithLicensesState): boolean {
  return selectCompanyName(state).length > 0;
}

export function selectLicenseCreatorId(state: IWithLicensesState): string {
  return selectLicense(state).createdBy;
}

export function selectLicenseSourceParams(state: IWithLicensesState): ILicenseSourceParams {
  return selectLicense(state).source;
}

export const selectHasSupportUkraineFooter = (state: IWithLicensesState): boolean =>
  selectLicense(state).settings.supportUkraineFooter;

export function selectTextEnhancements(state: IWithLicensesState): boolean {
  return Boolean(selectLicense(state)?.settings?.textEnhancements);
}

export function selectMoveToArchiveAfterDays(state: IWithLicensesState): number | null {
  return selectLicense(state).settings.moveToArchiveAfterDays;
}

export function selectMoveToTrashAfterDays(state: IWithLicensesState): number | null {
  return selectLicense(state).settings.moveToTrashAfterDays;
}

export function selectPurgeTrashAfterDays(state: IWithLicensesState): number {
  return selectLicense(state).settings.purgeTrashAfterDays;
}

export function selectPurgeSpamAfterDays(state: IWithLicensesState): number {
  return selectLicense(state).settings.purgeSpamAfterDays;
}

export function selectHasPoweredByFooter(state: IWithLicensesState): boolean {
  return Boolean(selectLicense(state).settings.poweredByFooter);
}

export const selectInvitationModeMigration = (state: IWithLicensesState): boolean =>
  Boolean(selectLicense(state).settings.invitationModeMigration);

export function selectIsCompanyNameSaving(state: IStoreState): boolean {
  return createRequestFetchingSelector(["LICENSE.COMPANY_NAME"])(state);
}

export function selectMessagingModeDataStatus(state: IWithLicensesState & IWithIntegrationsState): DataStatus {
  const hasLivechatIntegration = selectHasLiveChatIntegration(state);
  const properties = state.entities.license.livechatProperties;

  if (!hasLivechatIntegration) {
    return DataStatus.NotNeeded;
  }

  return properties ? (properties.status as DataStatus) : DataStatus.Loading;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function selectLivechatProperties(state: IWithLicensesState): undefined | null | Record<string, any> {
  const properties = state.entities.license.livechatProperties;

  return properties?.status === "success" ? properties.data : null;
}

export function selectMessagingModeEnabled(state: IWithLicensesState): boolean {
  const properties = selectLivechatProperties(state);

  return properties ? properties.continuous_chat_widget_enabled === "1" : false;
}

export function selectDefaultMailboxDescription(state: IWithLicensesState): string {
  return state.entities.license.settings.defaultMailboxDescription
    ? state.entities.license.settings.defaultMailboxDescription
    : "";
}

export function selectCustomFields(state: IWithLicensesState): ILicenseSettingsCustomFields {
  return selectLicense(state).settings.customFields || new Set();
}

export function selectTicketSummary(state: IWithLicensesState): boolean {
  return selectLicense(state).settings.ticketSummary;
}

export function selectTicketFeedToolbar(state: IWithLicensesState): boolean {
  return selectLicense(state).settings.ticketFeedToolbar;
}

export function selectFeatureToggleMacros(state: IWithLicensesState): boolean {
  return selectLicense(state).settings.featureToggleMacros;
}

export function selectFeatureToggleProductSwitcher(state: IWithLicensesState): boolean {
  return selectLicense(state).settings.featureToggleProductSwitcher;
}

export function selectFeatureToggleSimilarTickets(state: IWithLicensesState): boolean {
  return selectLicense(state).settings.featureToggleSimilarTickets;
}

export function selectFeatureToggleReplySuggestions(state: IWithLicensesState): boolean {
  return selectLicense(state).settings.featureToggleReplySuggestions;
}

export function selectMaxSharedMacros(state: IWithLicensesState): number {
  return selectLicense(state).settings.maxSharedMacros ?? Infinity;
}

export function selectMaxPrivateMacrosPerAgent(state: IWithLicensesState): number {
  return selectLicense(state).settings.maxPrivateMacrosPerAgent ?? Infinity;
}

export const selectMaxWebpushSubscriptionsPerAgent = (state: IWithLicensesState): number => {
  return selectLicense(state).settings.maxWebpushSubscriptionsPerAgent;
};

export function selectMaxCustomFieldsActive(state: IWithLicensesState): number {
  return selectLicense(state).settings.maxCustomFieldsActive ?? Infinity;
}

export const selectMaxTemplatesPerLicense = (state: IWithLicensesState): number => {
  return selectLicense(state).settings.maxTemplatesPerLicense ?? Infinity;
};

export function selectMaxEmailDomains(state: IWithLicensesState): number {
  return selectLicense(state).settings.maxEmailDomains ?? Infinity;
}

export function selectMaxContactForms(state: IWithLicensesState): number {
  return selectLicense(state).settings.maxContactForms ?? Infinity;
}

export function selectMaxMailboxes(state: IWithLicensesState): number {
  return selectLicense(state).settings.maxMailboxes ?? Infinity;
}

export function selectMaxPaidAgents(state: IWithLicensesState): number {
  return selectLicense(state).settings.maxPaidAgents ?? Infinity;
}

export function selectMaxReplyAddresses(state: IWithLicensesState): number {
  return selectLicense(state).settings.maxReplyAddresses ?? Infinity;
}

export function selectMaxRulesActive(state: IWithLicensesState): number {
  return selectLicense(state).settings.maxRulesActive ?? Infinity;
}

export function selectMaxTeams(state: IWithLicensesState): number {
  return selectLicense(state).settings.maxTeams ?? Infinity;
}

export function selectHasReportExportOption(state: IWithLicensesState): boolean {
  return selectLicense(state).settings.customReportGenerateAllowed;
}

export function selectHasAuditLog(state: IWithLicensesState): boolean {
  return selectLicense(state).settings.auditLog;
}

export function selectFeatureTogglePerformanceReport(state: IWithLicensesState): boolean {
  return selectLicense(state).settings.featureTogglePerformanceReport;
}

export function selectGoogleForwardingConfig(state: IWithLicensesState): IGoogleForwardingConfig | null {
  return selectLicense(state).settings.googleForwarding ?? null;
}

export function selectAutoassignmentManagedByAgents(state: IWithLicensesState): boolean {
  return selectLicense(state).settings.autoassignmentManagedByAgents;
}

export const selectCanChangeAutoassignment = createSelector(
  selectAutoassignmentManagedByAgents,
  selectIsAdmin,
  selectIsAgent,
  (isAutoassignmentManagedByAgents, isAdmin, isAgent) => {
    return (isAgent && isAutoassignmentManagedByAgents) || isAdmin;
  }
);

export function selectDetectedLanguages(state: IWithLicensesState): string[] {
  return selectLicense(state).detectedLanguages;
}

export function selectFeatureToggleSortOrder(state: IWithLicensesState): boolean {
  return selectLicense(state).settings.featureToggleSortOrder;
}

export function selectFeatureToggleTagSuggestions(state: IWithLicensesState): boolean {
  return selectLicense(state).settings.featureToggleTagSuggestions;
}

export function selectSignupGoogleAnalyticsEventSent(state: IWithLicensesState): boolean {
  return selectLicense(state).flags.signupGoogleAnalyticsEventSent;
}

export function selectIsNonProfitAccount(state: IWithLicensesState): boolean {
  return selectLicense(state).type === "nonprofit";
}

export function selectAvailableSources(state: IWithLicensesState): DetailedSource[] {
  return selectLicense(state).availableSources ?? [];
}
